/**
 * Makes inline code elements (not in code blocks) clickable to copy.
 */

import { BaseEnricher } from './BaseEnricher.js';
import type { EnricherConfig, EnricherContext } from './types.js';

export class InlineCodeEnricher extends BaseEnricher {
  constructor(config: EnricherConfig = {}) {
    super({ priority: 70, ...config });
  }

  async process(container: HTMLElement, _context: EnricherContext): Promise<void> {
    // Select all code elements not inside pre blocks
    container.querySelectorAll('code').forEach(code => {
      // Skip if inside a pre block (handled by CopyButtonEnricher)
      if (code.closest('pre')) return;

      const codeEl = code as HTMLElement;

      // Skip if already processed
      if (codeEl.dataset.copyable) return;
      codeEl.dataset.copyable = 'true';

      this.attachCopyHandler(codeEl);
    });
  }

  /**
   * Attach click handler to inline code element.
   */
  private attachCopyHandler(code: HTMLElement): void {
    code.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const text = code.textContent ?? '';
      try {
        await navigator.clipboard.writeText(text);
        this.showCopiedFeedback(code);
      } catch (err) {
        console.error('Failed to copy inline code:', err);
      }
    });
  }

  /**
   * Show visual feedback when inline code is copied.
   */
  private showCopiedFeedback(code: HTMLElement): void {
    code.classList.add('inline-copied', 'inline-copy-animate');

    setTimeout(() => {
      code.classList.remove('inline-copied', 'inline-copy-animate');
    }, 600);
  }
}
